import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { cn, dayjs } from '@noor/lib';

/** A day-precision date range. `to` omitted while the user is mid-selection. */
export interface DateRangeValue {
  from: Date;
  to?: Date;
}

/** A quick-pick range shown in the presets column. */
export interface DateRangePreset {
  /** Stable id (used for the active-state comparison + React key). */
  id: string;
  /** Bold title, e.g. "Сегодня". */
  label: string;
  /** Resolves the range at click time (so "today" is always current). */
  getRange: () => DateRangeValue;
}

export interface DateRangePickerProps {
  /** Currently applied range (drives the initial calendar month + fields). */
  value?: DateRangeValue | undefined;
  /** Called with the chosen range when the user presses Apply. */
  onApply: (range: DateRangeValue) => void;
  /** Preset quick-picks for the right column. */
  presets?: DateRangePreset[];
  /** Locale for month/weekday labels + field captions (defaults to `ru`). */
  locale?: string;
  /**
   * Full month names, January→December (12). Overrides the locale-derived names
   * — pass these when the runtime's `Intl` data for the locale is missing or
   * wrong (e.g. Latin Uzbek, which browsers format as "M06").
   */
  monthNames?: string[];
  /** Monday-first weekday abbreviations (7). Overrides the locale-derived ones. */
  weekdayLabels?: string[];
  labels?: Partial<DateRangePickerLabels>;
  className?: string;
}

export interface DateRangePickerLabels {
  selectedDates: string;
  from: string;
  to: string;
  day: string;
  month: string;
  year: string;
  apply: string;
}

const DEFAULT_LABELS: DateRangePickerLabels = {
  selectedDates: 'Выбранные даты',
  from: 'С',
  to: 'До',
  day: 'День',
  month: 'Месяц',
  year: 'Год',
  apply: 'Применить',
};

const FIELD_FORMAT = 'DD.MM.YYYY';

/**
 * The range-picker panel from the Figma design: a month calendar (Monday-first,
 * weekend headers in brand orange, the selected span filled orange) beside a
 * column of range presets, over a "Selected dates" footer whose editable
 * day/month/year fields mirror the calendar selection and commit on Apply.
 *
 * Presentational + self-contained: it owns only the *draft* selection and the
 * visible month; the caller owns the applied value and is notified on Apply.
 * `Filters.DateRange` wraps it to bind the range to URL search params; use it
 * directly anywhere a rich range picker is needed.
 */
export function DateRangePicker(props: DateRangePickerProps) {
  // Re-seed the draft from a new applied value by remounting the stateful inner
  // on `appliedKey` (keyed remount — no setState-in-effect). A fresh mount when
  // the popover reopens with a different range is exactly the reset we want.
  return <DateRangePickerInner key={rangeKey(props.value)} {...props} />;
}

function DateRangePickerInner({
  value,
  onApply,
  presets = [],
  locale = 'ru',
  monthNames,
  weekdayLabels,
  labels: labelOverrides,
  className,
}: DateRangePickerProps) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };

  // Draft selection (calendar clicks + field edits) — separate from the applied
  // `value` so nothing changes upstream until Apply. Seeded once from `value`;
  // the outer keyed remount handles re-seeding on a new applied range.
  const [draft, setDraft] = React.useState<DateRangeValue | undefined>(value);
  const [month, setMonth] = React.useState(() =>
    dayjs(value?.from ?? new Date()).startOf('month'),
  );

  // Figma 1613-35013 drafts everything and commits once, on Apply: a calendar
  // range, a preset pick, or field edits only reshape the draft. The draft is
  // complete (and Apply enabled) once both edges are set.
  const handleDayClick = (day: dayjs.Dayjs): void => {
    setDraft((prev) => nextSelection(prev, day.toDate()));
  };

  const handleFieldChange = (edge: 'from' | 'to', date: Date | undefined): void => {
    setDraft((prev) => applyFieldEdit(prev, edge, date));
    if (date) setMonth(dayjs(date).startOf('month'));
  };

  const handlePreset = (preset: DateRangePreset): void => {
    const range = preset.getRange();
    setDraft(range);
    setMonth(dayjs(range.from).startOf('month'));
  };

  const handleApply = (): void => {
    if (draft?.from && draft.to) onApply({ from: draft.from, to: draft.to });
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex flex-col gap-1 lg:flex-row lg:items-stretch">
        <CalendarCard
          month={month}
          draft={draft}
          locale={locale}
          monthNames={monthNames}
          weekdayLabels={weekdayLabels}
          onMonthChange={setMonth}
          onDayClick={handleDayClick}
        />
        {presets.length > 0 ? (
          <PresetsCard
            presets={presets}
            draft={draft}
            locale={locale}
            onPick={handlePreset}
            className="lg:w-[260px] lg:shrink-0"
          />
        ) : null}
      </div>

      <SelectedDatesBar
        draft={draft}
        labels={labels}
        onFieldChange={handleFieldChange}
        onApply={handleApply}
      />
    </div>
  );
}

/* ─────────────────────────── Calendar ─────────────────────────── */

function CalendarCard({
  month,
  draft,
  locale,
  monthNames,
  weekdayLabels: weekdayOverride,
  onMonthChange,
  onDayClick,
}: {
  month: dayjs.Dayjs;
  draft: DateRangeValue | undefined;
  locale: string;
  monthNames?: string[] | undefined;
  weekdayLabels?: string[] | undefined;
  onMonthChange: (m: dayjs.Dayjs) => void;
  onDayClick: (day: dayjs.Dayjs) => void;
}) {
  // Figma 1613-35014 renders only the weeks holding days of the month (five
  // for November 2024), not a fixed six-row grid.
  const weeks = React.useMemo(
    () =>
      buildMonthGrid(month).filter((week) =>
        week.some((day) => day.month() === month.month()),
      ),
    [month],
  );
  const weekdayLabels = React.useMemo(
    () => weekdayNames(locale, weekdayOverride),
    [locale, weekdayOverride],
  );
  // "Ноябрь, 2024" — month name + year, no era suffix (ru's `year:'numeric'`
  // would append " г."). Prefer a caller-supplied name (the runtime's `Intl`
  // data is wrong for some locales, e.g. Latin Uzbek → "M06"); else derive it.
  const monthName =
    monthNames?.[month.month()] ??
    capitalize(new Intl.DateTimeFormat(locale, { month: 'long' }).format(month.toDate()));
  const title = `${monthName}, ${month.year()}`;
  const today = dayjs();

  return (
    <div className="w-full shrink-0 rounded-2xl bg-card px-4 pt-4 pb-2 lg:w-[300px]">
      <div className="mb-2 flex items-center justify-between">
        <IconNavButton
          label="prev-month"
          onClick={() => onMonthChange(month.subtract(1, 'month'))}
        >
          <ChevronLeftIcon className="size-4" />
        </IconNavButton>
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <IconNavButton
          label="next-month"
          onClick={() => onMonthChange(month.add(1, 'month'))}
        >
          <ChevronRightIcon className="size-4" />
        </IconNavButton>
      </div>

      <div className="grid grid-cols-7 text-center">
        {weekdayLabels.map((wd) => (
          <div
            key={wd.key}
            className={cn(
              'text-xs font-medium',
              wd.weekend ? 'text-primary' : 'text-foreground',
            )}
          >
            {wd.label}
          </div>
        ))}
      </div>

      <div className="mt-2 grid grid-cols-7">
        {weeks.flat().map((day) => {
          const state = dayState(day, month, draft);
          const isToday = day.isSame(today, 'day');
          return (
            <button
              key={day.format('YYYY-MM-DD')}
              type="button"
              onClick={() => onDayClick(day)}
              aria-label={day.format(FIELD_FORMAT)}
              aria-pressed={state.selected}
              className={cn(
                'flex h-8 w-full items-center justify-center rounded-full text-sm transition-colors',
                'focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
                !state.inMonth && 'text-muted-foreground',
                state.inMonth &&
                  state.selected &&
                  'bg-primary text-primary-foreground hover:bg-primary',
                state.inMonth &&
                  !state.selected &&
                  state.inRange &&
                  'bg-primary/10 text-primary hover:bg-primary/20',
                state.inMonth &&
                  !state.selected &&
                  !state.inRange &&
                  isToday &&
                  'text-primary',
                state.inMonth &&
                  !state.selected &&
                  !state.inRange &&
                  !isToday &&
                  'text-foreground hover:bg-muted',
              )}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function IconNavButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-full text-primary transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── Presets ─────────────────────────── */

function PresetsCard({
  presets,
  draft,
  locale,
  onPick,
  className,
}: {
  presets: DateRangePreset[];
  draft: DateRangeValue | undefined;
  locale: string;
  onPick: (p: DateRangePreset) => void;
  className?: string;
}) {
  return (
    <div
      className={cn('flex w-full flex-col gap-1.5 rounded-3xl bg-card p-3', className)}
    >
      {presets.map((preset) => {
        const range = preset.getRange();
        const active = rangeKey(draft) === rangeKey(range);
        return (
          <button
            key={preset.id}
            type="button"
            onClick={() => onPick(preset)}
            aria-pressed={active}
            className={cn(
              'flex flex-1 flex-col justify-center gap-0.5 rounded-2xl px-3 py-1.5 text-left transition-colors',
              'focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none',
              active ? 'bg-primary' : 'bg-muted hover:bg-accent',
            )}
          >
            <span
              className={cn(
                'text-sm font-medium',
                active ? 'text-primary-foreground' : 'text-foreground',
              )}
            >
              {preset.label}
            </span>
            <span
              className={cn(
                'text-xs tabular-nums',
                active ? 'text-primary-foreground/90' : 'text-primary',
              )}
            >
              {formatPresetRange(range, locale)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────── Selected-dates bar ─────────────────────── */

function SelectedDatesBar({
  draft,
  labels,
  onFieldChange,
  onApply,
}: {
  draft: DateRangeValue | undefined;
  labels: DateRangePickerLabels;
  onFieldChange: (edge: 'from' | 'to', date: Date | undefined) => void;
  onApply: () => void;
}) {
  const complete = draft?.from != null && draft.to != null;
  return (
    <div className="rounded-3xl bg-card p-3">
      <p className="mb-2 text-sm font-medium text-foreground">{labels.selectedDates}</p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="shrink-0 text-xs font-medium text-foreground">
            {labels.from}
          </span>
          <DateFields
            edge="from"
            date={draft?.from}
            labels={labels}
            onChange={(d) => onFieldChange('from', d)}
          />
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="shrink-0 text-xs font-medium text-foreground">
            {labels.to}
          </span>
          <DateFields
            edge="to"
            date={draft?.to}
            labels={labels}
            onChange={(d) => onFieldChange('to', d)}
          />
        </div>

        <button
          type="button"
          onClick={onApply}
          disabled={!complete}
          className="h-10 shrink-0 rounded-full bg-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
        >
          {labels.apply}
        </button>
      </div>
    </div>
  );
}

/** The three day/month/year spinner-style fields for one edge of the range. */
function DateFields({
  edge,
  date,
  labels,
  onChange,
}: {
  edge: 'from' | 'to';
  date: Date | undefined;
  labels: DateRangePickerLabels;
  onChange: (date: Date | undefined) => void;
}) {
  const d = date ? dayjs(date) : undefined;

  const setPart = (part: 'date' | 'month' | 'year', raw: string): void => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    // Anchor edits on the current value, or today when the edge is still empty.
    const base = d ?? dayjs().startOf('day');
    const next =
      part === 'month'
        ? base.month(n - 1)
        : part === 'year'
          ? base.year(n)
          : base.date(n);
    if (next.isValid()) onChange(next.toDate());
  };

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <NumberField
        label={labels.day}
        value={d ? d.format('DD') : ''}
        min={1}
        max={31}
        onCommit={(v) => setPart('date', v)}
        ariaLabel={`${edge}-${labels.day}`}
      />
      <NumberField
        label={labels.month}
        value={d ? d.format('MM') : ''}
        min={1}
        max={12}
        onCommit={(v) => setPart('month', v)}
        ariaLabel={`${edge}-${labels.month}`}
      />
      <NumberField
        label={labels.year}
        value={d ? d.format('YYYY') : ''}
        min={1970}
        max={2100}
        onCommit={(v) => setPart('year', v)}
        ariaLabel={`${edge}-${labels.year}`}
      />
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: string;
  min: number;
  max: number;
  ariaLabel: string;
  onCommit: (value: string) => void;
}

/**
 * A single labelled pill input. Keyed on `value` so a new external value
 * remounts it with a fresh draft (no setState-in-effect re-seed).
 */
function NumberField({ value, ...rest }: NumberFieldProps) {
  return <NumberFieldInner key={value} value={value} {...rest} />;
}

function NumberFieldInner({
  label,
  value,
  min,
  max,
  ariaLabel,
  onCommit,
}: NumberFieldProps) {
  const [draft, setDraft] = React.useState(value);

  const commit = (): void => {
    if (draft.trim() === '') return;
    const clamped = Math.min(max, Math.max(min, Number(draft) || min));
    onCommit(String(clamped));
  };

  return (
    <label className="flex h-10 min-w-0 flex-1 flex-col justify-center rounded-[52px] bg-muted px-3 transition-shadow focus-within:ring-2 focus-within:ring-ring">
      <span className="text-[10px] leading-tight font-medium text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        inputMode="numeric"
        value={draft}
        min={min}
        max={max}
        aria-label={ariaLabel}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        // Wheel-over-a-focused-number-field would silently change the year;
        // blurring yields the scroll to the page (and commits the draft, same
        // as tabbing away) rather than trapping it with `preventDefault`.
        onWheel={(e) => {
          if (e.currentTarget === document.activeElement) e.currentTarget.blur();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            e.currentTarget.blur();
          }
        }}
        className="w-full [appearance:textfield] bg-transparent text-xs font-medium text-foreground outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />
    </label>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

/** Six weeks of days (Monday-first) covering `month`, incl. spillover days. */
function buildMonthGrid(month: dayjs.Dayjs): dayjs.Dayjs[][] {
  const start = month.startOf('month').startOf('isoWeek');
  const weeks: dayjs.Dayjs[][] = [];
  let cursor = start;
  for (let w = 0; w < 6; w += 1) {
    const week: dayjs.Dayjs[] = [];
    for (let d = 0; d < 7; d += 1) {
      week.push(cursor);
      cursor = cursor.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
}

/**
 * Monday-first weekday abbreviations, with weekend flags. Uses `override` when
 * given (7 labels, Mon→Sun) — the runtime's `Intl` short-weekday data is wrong
 * for some locales (Latin Uzbek falls back to English); else derives from `locale`.
 */
function weekdayNames(
  locale: string,
  override?: string[],
): { key: string; label: string; weekend: boolean }[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // 2024-01-01 is a Monday — walk Mon→Sun.
  const monday = dayjs('2024-01-01');
  return Array.from({ length: 7 }, (_, i) => {
    const day = monday.add(i, 'day');
    const label = override?.[i] ?? capitalize(fmt.format(day.toDate()).replace('.', ''));
    return {
      key: String(i),
      label,
      weekend: i >= 5,
    };
  });
}

interface DayState {
  inMonth: boolean;
  selected: boolean;
  inRange: boolean;
}

function dayState(
  day: dayjs.Dayjs,
  month: dayjs.Dayjs,
  draft: DateRangeValue | undefined,
): DayState {
  const inMonth = day.month() === month.month();
  if (!draft?.from) {
    return { inMonth, selected: false, inRange: false };
  }
  const from = dayjs(draft.from).startOf('day');
  const to = draft.to ? dayjs(draft.to).startOf('day') : undefined;
  const isFrom = day.isSame(from, 'day');
  const isTo = to ? day.isSame(to, 'day') : false;
  const inRange = to ? !day.isBefore(from, 'day') && !day.isAfter(to, 'day') : isFrom;
  return { inMonth, selected: isFrom || isTo, inRange };
}

/** Click semantics: start a new range, or close an open one (ordered). */
function nextSelection(prev: DateRangeValue | undefined, clicked: Date): DateRangeValue {
  if (!prev?.from || prev.to) {
    return { from: clicked };
  }
  const from = dayjs(prev.from);
  if (dayjs(clicked).isBefore(from, 'day')) {
    return { from: clicked, to: prev.from };
  }
  return { from: prev.from, to: clicked };
}

/** Apply a field edit to one edge, keeping from ≤ to. */
function applyFieldEdit(
  prev: DateRangeValue | undefined,
  edge: 'from' | 'to',
  date: Date | undefined,
): DateRangeValue | undefined {
  if (edge === 'from') {
    if (!date) return prev?.to ? { from: prev.to } : undefined;
    const to = prev?.to;
    if (to && dayjs(date).isAfter(dayjs(to), 'day')) return { from: date };
    return { from: date, ...(to ? { to } : {}) };
  }
  // edge === 'to'
  if (!date) return prev?.from ? { from: prev.from } : undefined;
  const from = prev?.from ?? date;
  if (dayjs(date).isBefore(dayjs(from), 'day')) return { from: date, to: from };
  return { from, to: date };
}

function formatPresetRange(range: DateRangeValue, locale: string): string {
  const fmt = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const from = fmt.format(range.from);
  if (!range.to || dayjs(range.from).isSame(range.to, 'day')) return from;
  return `${from} – ${fmt.format(range.to)}`;
}

/** Stable comparison key for a range (ignores time). */
function rangeKey(range: DateRangeValue | undefined): string {
  if (!range?.from) return '';
  const from = dayjs(range.from).format('YYYY-MM-DD');
  const to = range.to ? dayjs(range.to).format('YYYY-MM-DD') : from;
  return `${from}|${to}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Labels for the standard preset set. */
export interface DateRangePresetLabels {
  yesterday: string;
  today: string;
  thisWeek: string;
  thisMonth: string;
  thisYear: string;
}

/**
 * The five standard quick-picks from the design (yesterday → this year),
 * resolved lazily so "today" is always current. Ranges use the ISO (Monday-
 * first) week to match the calendar grid.
 */
export function standardDateRangePresets(
  labels: DateRangePresetLabels,
): DateRangePreset[] {
  return [
    {
      id: 'yesterday',
      label: labels.yesterday,
      getRange: () => {
        const d = dayjs().subtract(1, 'day').startOf('day').toDate();
        return { from: d, to: d };
      },
    },
    {
      id: 'today',
      label: labels.today,
      getRange: () => {
        const d = dayjs().startOf('day').toDate();
        return { from: d, to: d };
      },
    },
    {
      id: 'this-week',
      label: labels.thisWeek,
      getRange: () => ({
        from: dayjs().startOf('isoWeek').toDate(),
        to: dayjs().startOf('day').toDate(),
      }),
    },
    {
      id: 'this-month',
      label: labels.thisMonth,
      getRange: () => ({
        from: dayjs().startOf('month').toDate(),
        to: dayjs().startOf('day').toDate(),
      }),
    },
    {
      id: 'this-year',
      label: labels.thisYear,
      getRange: () => ({
        from: dayjs().startOf('year').toDate(),
        to: dayjs().startOf('day').toDate(),
      }),
    },
  ];
}
